import type { AvgSection } from '@/types/pages/AvgContent.ts'

const sections: AvgSection[] = [
  {
    title: 'A. Verarbeitung personenbezogener Daten',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo verarbeitet personenbezogene Daten im Auftrag des Restaurants zur Erfüllung des Vertrags, einschließlich der Verarbeitung von Bestellungen, Zahlungen und der Erbringung von Dienstleistungen. Darüber hinaus werden anonymisierte Daten für statistische Zwecke verwendet.',
      },
      {
        type: 'paragraph',
        text: 'Zu den verarbeiteten Daten gehören unter anderem: Name, Adresse, E-Mail-Adresse, Telefonnummer, Zahlungsdaten, IP-Adresse sowie Geräte- und Browserdaten.',
      },
      {
        type: 'paragraph',
        text: 'Bistroo handelt als Auftragsverarbeiter, das Restaurant als Verantwortlicher.',
      },
    ],
  },
  {
    title: '1. Begriffsbestimmungen',
    blocks: [
      {
        type: 'paragraph',
        text: 'In diesen Bedingungen (nachfolgend: „Auftragsverarbeitungsbedingungen“) wird eine Reihe von Begriffen verwendet, deren Bedeutung nachstehend erläutert wird. Diese Begriffe werden in den Auftragsverarbeitungsbedingungen großgeschrieben. Die nachfolgende Aufzählung orientiert sich überwiegend an den Begriffsbestimmungen der Datenschutzgesetze und -vorschriften.',
      },
      {
        type: 'definition',
        term: 'Betroffene Person',
        text: 'Die Person, auf die sich ein personenbezogenes Datum bezieht.',
      },
      {
        type: 'definition',
        term: 'Auftragsverarbeiter',
        text: 'Eine natürliche oder juristische Person, Behörde, Einrichtung oder andere Stelle, die personenbezogene Daten im Auftrag des Verantwortlichen verarbeitet, ohne dessen unmittelbarer Weisungsbefugnis zu unterstehen.',
      },
      {
        type: 'definition',
        term: 'Unterauftragsverarbeiter',
        text: 'Ein weiterer Auftragsverarbeiter, der vom Auftragsverarbeiter eingesetzt wird, um im Auftrag des Verantwortlichen bestimmte Verarbeitungstätigkeiten durchzuführen.',
      },
      {
        type: 'definition',
        term: 'Verantwortlicher',
        text: 'Eine natürliche oder juristische Person, Behörde, Einrichtung oder andere Stelle, die allein oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung personenbezogener Daten entscheidet.',
      },
      {
        type: 'definition',
        term: 'Besondere Kategorien personenbezogener Daten',
        text: 'Daten, aus denen die rassische oder ethnische Herkunft, politische Meinungen, religiöse oder weltanschauliche Überzeugungen oder die Gewerkschaftszugehörigkeit hervorgehen, sowie genetische Daten, biometrische Daten zur eindeutigen Identifizierung einer Person, Gesundheitsdaten oder Daten zum Sexualleben oder der sexuellen Orientierung einer Person. Ebenso personenbezogene Daten über strafrechtliche Verurteilungen und Straftaten oder damit zusammenhängende Sicherungsmaßnahmen.',
      },
      {
        type: 'definition',
        term: 'Datenpanne / Verletzung des Schutzes personenbezogener Daten',
        text: 'Eine Verletzung der Sicherheit, die versehentlich oder unrechtmäßig zur Vernichtung, zum Verlust, zur Veränderung oder zur unbefugten Offenlegung von bzw. zum unbefugten Zugang zu übermittelten, gespeicherten oder auf sonstige Weise verarbeiteten personenbezogenen Daten führt - oder bei der vernünftigerweise nicht ausgeschlossen werden kann, dass sie dazu führt.',
      },
      {
        type: 'definition',
        term: 'Dritte',
        text: 'Andere als das Restaurant, Bistroo und deren Mitarbeiter.',
      },
      {
        type: 'definition',
        term: 'Meldepflicht bei Datenpannen',
        text: 'Die Pflicht, Datenpannen der niederländischen Datenschutzbehörde (Autoriteit Persoonsgegevens) und (in bestimmten Fällen) der bzw. den betroffenen Person(en) zu melden.',
      },
      {
        type: 'definition',
        term: 'Mitarbeiter',
        text: 'Personen, die bei Bistroo oder beim Restaurant tätig sind, sei es im Rahmen eines Arbeitsverhältnisses oder vorübergehend beauftragt.',
      },
      {
        type: 'definition',
        term: 'Personenbezogene Daten',
        text: 'Alle Informationen, die sich auf eine identifizierte oder identifizierbare natürliche Person („betroffene Person“) beziehen und im Rahmen des Vertrags verarbeitet werden; als identifizierbar wird eine natürliche Person angesehen, die direkt oder indirekt identifiziert werden kann, insbesondere mittels Zuordnung zu einer Kennung wie einem Namen, einer Kennnummer, zu Standortdaten, zu einer Online-Kennung oder zu einem oder mehreren besonderen Merkmalen, die Ausdruck der physischen, physiologischen, genetischen, psychischen, wirtschaftlichen, kulturellen oder sozialen Identität dieser natürlichen Person sind.',
      },
      {
        type: 'definition',
        term: 'Sensible personenbezogene Daten',
        text: 'Personenbezogene Daten, deren Verlust oder unrechtmäßige Verarbeitung (unter anderem) zu Stigmatisierung oder Ausgrenzung der betroffenen Person, zu gesundheitlichen Schäden, zu finanziellen Schäden oder zu (Identitäts-)Betrug führen kann.',
      },
      {
        type: 'paragraph',
        text: 'Zu diesen Kategorien personenbezogener Daten gehören in jedem Fall:',
      },
      {
        type: 'list',
        items: [
          'Besondere Kategorien personenbezogener Daten',
          'Daten über die finanzielle oder wirtschaftliche Situation der betroffenen Person',
          '(Sonstige) Daten, die zu Stigmatisierung oder Ausgrenzung der betroffenen Person führen können',
          'Benutzernamen, Passwörter und sonstige Anmeldedaten',
          'Daten, die für (Identitäts-)Betrug missbraucht werden können',
        ],
      },
      {
        type: 'definition',
        term: 'Verarbeiten / Verarbeitung',
        text: 'Jeder mit oder ohne Hilfe automatisierter Verfahren ausgeführte Vorgang oder jede solche Vorgangsreihe im Zusammenhang mit personenbezogenen Daten wie das Erheben, das Erfassen, die Organisation, das Ordnen, die Speicherung, die Anpassung oder Veränderung, das Auslesen, das Abfragen, die Verwendung, die Offenlegung durch Übermittlung, Verbreitung oder eine andere Form der Bereitstellung, den Abgleich oder die Verknüpfung, die Einschränkung, das Löschen oder die Vernichtung von Daten.',
      },
      {
        type: 'definition',
        term: 'Auftragsverarbeitungsbedingungen',
        text: 'Diese Bedingungen (die zugleich als Auftragsverarbeitungsvertrag gelten).',
      },
      {
        type: 'definition',
        term: 'DSGVO',
        text: 'Die Datenschutz-Grundverordnung, einschließlich des niederländischen Durchführungsgesetzes zu dieser Verordnung.',
      },
    ],
  },
  {
    title: '2. Anwendbarkeit und Laufzeit',
    blocks: [
      {
        type: 'paragraph',
        text: 'Diese Auftragsverarbeitungsbedingungen gelten für alle Verarbeitungen durch Bistroo und laufen, solange Bistroo personenbezogene Daten verarbeitet. Eine Kündigung ist nicht möglich. Vertraulichkeit und Haftung gelten auch nach Beendigung fort.',
      },
    ],
  },
  {
    title: '3. Verarbeitung',
    blocks: [
      {
        type: 'list',
        items: [
          'Bistroo verarbeitet Daten ausschließlich nach den Weisungen des Restaurants.',
          'Zweck und Mittel der Verarbeitung werden vom Restaurant bestimmt.',
          'Bistroo hält die Datenschutzgesetze ein; das Restaurant muss über eine Rechtsgrundlage für die Verarbeitung verfügen.',
          'Der Zugriff auf Daten ist auf befugte Mitarbeiter beschränkt.',
          'Bistroo darf Unterauftragsverarbeiter unter gleichen Verpflichtungen einsetzen.',
          'Bistroo unterstützt bei Anfragen betroffener Personen.',
          'Die Verarbeitung erfolgt vorzugsweise innerhalb des EWR, andernfalls mit geeigneten Garantien.',
          'Anfragen von Behörden werden rechtlich geprüft und, soweit möglich, dem Restaurant mitgeteilt.',
        ],
      },
    ],
  },
  {
    title: '4. Sicherheit',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo trifft geeignete technische und organisatorische Maßnahmen. Das Restaurant kann auf eigene Kosten Audits durchführen.',
      },
    ],
  },
  {
    title: '5. Datenpannen',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo meldet Datenpannen so schnell wie möglich (angestrebte Frist: 48 Stunden). Die Meldepflicht gegenüber der Aufsichtsbehörde und den betroffenen Personen liegt beim Restaurant.',
      },
    ],
  },
  {
    title: '6. Vertraulichkeit',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo und ihre Mitarbeiter/Unterauftragsverarbeiter sind zur Vertraulichkeit verpflichtet.',
      },
    ],
  },
  {
    title: '7. Haftung',
    blocks: [
      {
        type: 'paragraph',
        text: 'Das Restaurant ist für die rechtmäßige Verarbeitung verantwortlich und stellt Bistroo bei Verstößen gegen Datenschutzgesetze frei. Die Haftungsbeschränkungen aus dem Vertrag gelten auch hier.',
      },
    ],
  },
  {
    title: '8. Übertragung',
    blocks: [
      {
        type: 'paragraph',
        text: 'Das Restaurant darf Rechte/Pflichten nicht übertragen; Bistroo schon.',
      },
    ],
  },
  {
    title: '9. Beendigung und Daten',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bei Beendigung des Vertrags werden die Daten zurückgegeben oder vernichtet, sofern keine gesetzliche Aufbewahrungspflicht besteht. Die Kosten trägt das Restaurant.',
      },
    ],
  },
  {
    title: '10. Änderungen',
    blocks: [
      {
        type: 'paragraph',
        text: 'Änderungen sind nur wirksam, wenn sie schriftlich vereinbart wurden (einschließlich per E-Mail).',
      },
    ],
  },
  {
    title: '11. Schlussbestimmungen',
    blocks: [
      {
        type: 'list',
        items: [
          'Bistroo stellt auf Anfrage Informationen über die Einhaltung zur Verfügung.',
          'Die Parteien arbeiten mit den Aufsichtsbehörden zusammen.',
          'Es gilt niederländisches Recht; zuständig sind die niederländischen Gerichte.',
          'Unwirksame Bestimmungen werden durch rechtlich wirksame gleichwertige Bestimmungen ersetzt.',
        ],
      },
    ],
  },
]

export const avg = {
  seo: {
    title: "Auftragsverarbeitungsbedingungen (DSGVO) {'|'} Bistroo",
    description:
      'Die Auftragsverarbeitungsbedingungen von Bistroo: wie wir personenbezogene Daten im Auftrag von Restaurants verarbeiten, im Einklang mit der DSGVO.',
  },
  title: 'Auftragsverarbeitungsbedingungen',
  subtitle: 'Anlage 1 – Auftragsverarbeitungsbedingungen (DSGVO)',
  sections,
}
